import React, { useState, useEffect } from 'react';
import { Send } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../hooks/auth/useAuth';
import { useProfile } from '../../hooks/auth/useProfile';
import { supabase } from '../../lib/supabase';

interface Comment {
  id: string;
  content: string;
  created_at: string;
  user_profiles: {
    first_name: string;
    last_name: string;
    is_admin: boolean;
  };
}

interface ResumeCommentsProps {
  reviewId: string;
}

export function ResumeComments({ reviewId }: ResumeCommentsProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const { user } = useAuth();
  const { profile } = useProfile();

  useEffect(() => {
    if (reviewId) {
      loadComments();
    }
  }, [reviewId]);

  const loadComments = async () => {
    if (!reviewId) return;
    setIsLoadingComments(true);
    try {
      const { data, error } = await supabase
        .from('resume_review_comments')
        .select('*, user_profiles(*)')
        .eq('review_id', reviewId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setComments(data || []);
    } catch (error) {
      console.error('Error loading comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('resume_review_comments')
        .insert({
          review_id: reviewId,
          user_id: user.id,
          content: newComment.trim()
        });

      if (error) throw error;
      
      setNewComment('');
      await loadComments();
      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {isLoadingComments ? (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#800020] border-t-transparent"></div>
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-4 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No comments yet. Be the first to comment!</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
          {comments.map((comment) => (
            <div key={comment.id} className="flex space-x-3">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-[#800020]/10 flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-600">
                    {comment.user_profiles.first_name[0]}
                    {comment.user_profiles.last_name[0]}
                  </span>
                </div>
              </div>
              <div className="flex-1 bg-gray-50 rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-medium text-black">
                      {comment.user_profiles.first_name} {comment.user_profiles.last_name}
                    </h4>
                    {comment.user_profiles.is_admin && (
                      <span className="px-2 py-0.5 bg-[#800020] text-white text-xs font-bold rounded-full">
                        Admin
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(comment.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="mt-1 text-sm text-black">
                  {comment.content}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmitComment} className="mt-4">
        <div className="flex items-center space-x-3">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            className="flex-1 min-w-0 bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm text-black placeholder-gray-400 focus:ring-2 focus:ring-[#800020] focus:border-[#800020] transition-colors"
          />
          <button
            type="submit"
            disabled={isLoading || !newComment.trim()}
            className="inline-flex items-center px-4 py-3 bg-[#800020] text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}